import _get from 'lodash/get';
import { call, put, select } from 'redux-saga/effects';

import RavenError from 'ravenjs/lib/Error';

import { MFA_ERROR_TYPES } from 'constants/mfa';
import { registerMFAAPI } from 'modules/apis/mfa';

import * as mfaActions from '../actions';
import * as mfaSelectors from '../reducer';

/**
 * Start the enrollment of the user into the method they picked on the setup
 * screen, i.e. register the method against the MFA API and keep the shared
 * secret and the `otpauth://` URI the API answers with.
 *
 * The request is authorized with the `mfaToken` of the authenticate response
 * alone, the method is decided by the stage of that token, so an enrollment
 * attempted without an MFA session never reaches the API and carries the
 * expired session it stands for.
 *
 * NOTE: the secret and the QR data of the response are put in the non persisted
 * 'mfa' state and nowhere else, they are never logged nor reported. The error is
 * stored as is, the setup screen maps its `errorCode` to the state it renders.
 *
 * @method registerMFAMethodSaga
 * @param  {Object}                [action] The action, `payload.isSettings` enrolls the signed
 *                                          in user from the account settings
 * @type   {Generator}
 */
export function* registerMFAMethodSaga(action = {}) {
    try {
        yield put(mfaActions.registerMFAMethod.request());

        // The signed in user enrolls from the account settings with the access token.
        const isSettings = Boolean(_get(action, 'payload.isSettings', false));
        let mfaToken = '';

        if (!isSettings) {
            mfaToken = yield select(mfaSelectors.getMFAToken);

            if (!mfaToken) {
                const noSessionError = new RavenError('There is no MFA session in progress.');

                noSessionError.mfaErrorType = MFA_ERROR_TYPES.SESSION_EXPIRED;

                throw noSessionError;
            }
        }

        const response = yield call(registerMFAAPI, isSettings ? {} : { mfaToken });
        const data = _get(response, 'data', {});
        const secret = _get(data, 'secret', '');
        const qrCode = _get(data, 'qrCode', '');

        if (!secret || !qrCode) {
            throw new RavenError('The MFA enrollment could not be started.');
        }

        yield put(
            mfaActions.registerMFAMethod.success({
                qrCode,
                secret,
                status: _get(data, 'status', ''),
            })
        );
    } catch (error) {
        yield put(mfaActions.registerMFAMethod.error(error));
    }
}

export default registerMFAMethodSaga;
